import { Card, Col, Input, Row, Select, Typography } from "antd";
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useIntl } from "react-intl"; // Import react-intl
import "../../../../app/checkout/page.css";
import { countries, formatCallingCode } from "../../../../Lib/Utils/countries";
import { matchesCountryTerm } from "../../../../Lib/Utils/countryFilter";
import FlagImage from "../../../Shared/FlagImage";
import RedCaretIcon from "../../../Shared/RedCaretIcon";
import CountrySearchPopup from "../../../Common/Inputs/CountrySearchPopup";

const { Option } = Select;

const ManagedInput = ({ value, onChange, onBlur, ...props }: any) => {
  const [localValue, setLocalValue] = useState(value);
  const inputRef = useRef<any>(null);

  useEffect(() => {
    if (document.activeElement !== inputRef.current?.input) {
      setLocalValue(value);
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
    if (onChange) {
      onChange(e);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setLocalValue(value);
    if (onBlur) {
      onBlur(e);
    }
  };

  return (
    <Input
      {...props}
      ref={inputRef}
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
};

type CustomerContact = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  countryCode?: string;
};

type CustomerProps = {
  customerContact: CustomerContact;
  handleCustomerContactChange: (
    key: keyof CustomerContact,
    value: string
  ) => void;
};

export function CustomerContactForm({
  customerContact,
  handleCustomerContactChange,
}: CustomerProps) {
  const intl = useIntl();
  const [errors, setErrors] = useState({
    email: "",
    mobile: "",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [isPhoneNumberTouched, setIsPhoneNumberTouched] = useState(false);

  useEffect(() => {
    let currentCountryCode = getCountryCode(customerContact.phoneNumber);
    setErrors({
      email: validateEmail(customerContact.email)
        ? ""
        : intl.formatMessage({
          id: "invalidEmail",
          defaultMessage: "Please enter a valid email",
        }),
      mobile:
        customerContact.phoneNumber &&
          !validateMobile(
            customerContact.phoneNumber.replace(currentCountryCode, ""),
            currentCountryCode
          )
          ? intl.formatMessage({
            id: "invalidMobile",
            defaultMessage: "Please enter a valid mobile number",
          })
          : "",
    });
  }, [customerContact.email]);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    /**
     * I removed email "" validation because,
     * It't email === "" is no more validate.
     * So I bypass the email === "" validation.
    */
    if (email === "") return true;
    return emailRegex.test(email);
  };

  const validateMobile = (mobile: string, countryCode: string) => {
    const country = countries.find((c) => c.countryCallingCode === countryCode);
    if (!country) return false;
    return mobile.length === country.phoneLength;
  };

  const validateName = (name: string) => {
    // Allow only letters and spaces
    const sanitized = name.replace(/[^\p{L}\p{M}\s-]/gu, "");

    // Limit to 50 characters
    return sanitized.length > 50 ? sanitized.slice(0, 50) : sanitized;
  };

  const filteredCountries = useMemo(
    () => countries.filter((c) => matchesCountryTerm(c, searchTerm)),
    [searchTerm],
  );

  const getCountryCode = (phoneNumber: string) => {
    if (phoneNumber) {
      const country = countries.find((country) =>
        phoneNumber.startsWith(country.countryCallingCode)
      );
      return country ? country.countryCallingCode : "";
    }
    return countries[0]?.countryCallingCode || "";
  };

  const extractPhoneNumber = (phoneNumber: string) => {
    if (phoneNumber) {
      const country = countries.find((country) =>
        phoneNumber.startsWith(country.countryCallingCode)
      );
      return country
        ? phoneNumber.slice(country.countryCallingCode.length)
        : phoneNumber;
    }
    return phoneNumber;
  };

  return (
    <>
      <Row gutter={[20, 20]} className="customer-contact-form">
        <Col span={12}>
          <div className="input-wrapper">
            <label htmlFor="firstNameContact">
              {intl.formatMessage({ id: "firstName" })}
            </label>
            <ManagedInput
              id="firstNameContact"
              placeholder={intl.formatMessage({ id: "firstNamePlaceholder" })}
              value={customerContact.firstName || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const validName = validateName(e.target.value);
                handleCustomerContactChange("firstName", validName);
              }}
              className="radius-12"
            />
          </div>
        </Col>
        <Col span={12}>
          <div className="input-wrapper">
            <label htmlFor="lastNameContact">
              {intl.formatMessage({ id: "lastName" })}
            </label>
            <ManagedInput
              id="lastNameContact"
              placeholder={intl.formatMessage({ id: "lastNamePlaceholder" })}
              value={customerContact.lastName || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const validName = validateName(e.target.value);
                handleCustomerContactChange("lastName", validName);
              }}
              className="radius-12"
            />
          </div>
        </Col>
        <Col span={12}>
          <div className="input-wrapper position-relative">
            <label htmlFor="emailContact">
              {intl.formatMessage({ id: "email" })}
            </label>
            <ManagedInput
              id="emailContact"
              placeholder={intl.formatMessage({ id: "emailPlaceholder" })}
              value={customerContact.email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                handleCustomerContactChange("email", e.target.value);
                setErrors((prev) => ({
                  ...prev,
                  email: validateEmail(e.target.value)
                    ? ""
                    : intl.formatMessage({
                      id: "invalidEmail",
                      defaultMessage: "Please enter a valid email",
                    }),
                }));
              }}
              status={errors.email ? "error" : ""}
              className="radius-12"
            />
            {errors.email && (
              <div className="error-message">{errors.email}</div>
            )}
          </div>
        </Col>

        <Col span={12}>
          <div className="input-wrapper position-relative">
            <label htmlFor="mobileContact">
              {intl.formatMessage({ id: "mobile" })}
            </label>
            <div id="countryCode" className="d-flex w-100">
              <Select
                value={getCountryCode(customerContact.phoneNumber)}
                onChange={(value) => {
                  const currentPhoneNumber = extractPhoneNumber(
                    customerContact.phoneNumber
                  );
                  const newPhoneNumber = value + currentPhoneNumber;
                  handleCustomerContactChange("phoneNumber", newPhoneNumber);
                }}
                aria-label="combobox"
                suffixIcon={<RedCaretIcon />}
                className="country-code-select"
                styles={{ popup: { root: { width: "360px", maxWidth: "360px" } } }}
                optionLabelProp="label"
                onOpenChange={(open) => {
                  if (!open) setSearchTerm("");
                }}
                popupRender={(menu) => (
                  <CountrySearchPopup
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder={intl.formatMessage({ id: "searchCountryOrCode" })}
                    inputClassName="country-search-input"
                  >
                    {menu}
                  </CountrySearchPopup>
                )}
              >
                {filteredCountries.map((country) => (
                  <Option
                    key={country.id}
                    value={country.countryCallingCode}
                    label={formatCallingCode(country.countryCallingCode)}
                    className="country-code-option"
                  >
                    <div className="country-option d-flex gap-10 align-items-center">
                      <FlagImage countryCode={country.countryCode} />
                      <span className="country-label">{country.label}</span>
                    </div>
                    <span>{formatCallingCode(country.countryCallingCode)}</span>
                  </Option>
                ))}
              </Select>
              <ManagedInput
                id="mobileContact"
                placeholder={intl.formatMessage({ id: "mobilePlaceholder" })}
                value={extractPhoneNumber(customerContact.phoneNumber)}
                maxLength={(() => {
                  const currentCountryCode = getCountryCode(
                    customerContact.phoneNumber
                  );
                  const country = countries.find(
                    (c) => c.countryCallingCode === currentCountryCode
                  );
                  return country ? country.phoneLength : 10; // Default to 10 if country not found
                })()}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const currentCountryCode = getCountryCode(
                    customerContact.phoneNumber
                  );
                  const value = e.target.value.replace(/\D/g, "");
                  const newPhoneNumber = currentCountryCode + value;
                  handleCustomerContactChange("phoneNumber", newPhoneNumber);
                  setIsPhoneNumberTouched(true);
                  if (value) {
                    setErrors((prev) => ({
                      ...prev,
                      mobile: validateMobile(value, currentCountryCode)
                        ? ""
                        : intl.formatMessage({
                          id: "invalidMobile",
                          defaultMessage:
                            "Please enter a valid mobile number",
                        }),
                    }));
                  } else {
                    setErrors((prev) => ({ ...prev, mobile: "" }));
                  }
                }}
                status={errors.mobile && isPhoneNumberTouched ? "error" : ""}
                className="w-100"
                style={{
                  borderRadius:
                    intl?.locale === "en"
                      ? "0px 12px 12px 0px"
                      : "12px 0 0 12px",
                  borderLeft: intl?.locale === "en" ? "transparent" : "",
                  borderRight: intl?.locale === "ar" ? "transparent" : "",
                }}
              />
            </div>
            {errors.mobile && isPhoneNumberTouched && (
              <div className="error-message">{errors.mobile}</div>
            )}
          </div>
        </Col>
      </Row>
    </>
  );
}

export default function CustomerContact(props: CustomerProps) {
  const intl = useIntl();

  return (
    <Card className="contact-section">
      <Typography.Title
        level={3}
        className="fs-16 fw-600 ff-inter mb-2 color-2F3034"
      >
        {intl.formatMessage({ id: "customerContact" })}
      </Typography.Title>
      <p className="confirmation-message">
        {intl.formatMessage({ id: "bookingConfirmationMessage" })}
      </p>

      <CustomerContactForm
        customerContact={props.customerContact}
        handleCustomerContactChange={props.handleCustomerContactChange}
      />
    </Card>
  )
}
